import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { AttemptsWithoutPassingQuery } from '../../graphQLOperations/__generated__/AttemptsWithoutPassingQuery.graphql';
import { attemptsWithoutPassing } from '../../graphQLOperations/AttemptsWithoutPassing';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { useEffect, useState } from 'react';
import CopyListOfStudentsButton from './CopyListOfStudentsButton';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  queryReference: PreloadedQuery<AttemptsWithoutPassingQuery, Record<string, unknown>>;
}

interface StudentData {
  firstName: string;
  lastName: string;
  publicUserId: string;
}

const SkillChallengeAttemptsWithoutPassing = ({ queryReference }: Props) => {
  const PAGE_SIZE = 10;
  const [first, setFirst] = useState(0);
  const { studentsAttemptedSkillsWithoutPassing } = usePreloadedQuery<AttemptsWithoutPassingQuery>(
    attemptsWithoutPassing,
    queryReference,
  );

  const sortByLastNameThenFirstName = (a: StudentData, b: StudentData) =>
    (a.lastName || '').localeCompare(b.lastName || '', undefined, { sensitivity: 'base' }) ||
    (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' });

  const sortedStudents = [...(studentsAttemptedSkillsWithoutPassing as StudentData[])].sort(
    sortByLastNameThenFirstName,
  );

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
  };

  useEffect(() => {
    setFirst(0);
  }, [queryReference]);

  return (
    <div id="ThreePlusWeekly" className="lg:col-8 col-12 border-300 md:h-17rem mt-2 border-2 border-round-xl p-4">
      <div className="flex justify-content-between font-bold flex-column sm:flex-row ">
        <p className="font-semibold text-base" style={{ fontFamily: 'Poppins' }}>
          3+ Skill Challenge <span style={{ color: '#B71C1B' }}>attempts without passing</span>
          <span className="pi pi-exclamation-triangle ml-1" style={{ color: '#B71C1B' }}></span>
        </p>
        {sortedStudents.length ? <CopyListOfStudentsButton studentList={sortedStudents} /> : null}
      </div>
      {sortedStudents.length ? (
        <>
          <div className="flex sm:justify-content-start sm:h-8rem justify-content-center ">
            <ul className="flex flex-column flex-wrap list-none w-full pl-0">
              {sortedStudents.slice(first, PAGE_SIZE + first).map(student => (
                <li key={student.publicUserId}>
                  <MaskedStudentData
                    type={PiiTypes.FullName}
                    student={student}
                  >{`${student.firstName} ${student.lastName}`}</MaskedStudentData>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex align-items-center justify-content-center mt-0 h-3rem">
            <Paginator
              first={first}
              rows={PAGE_SIZE}
              totalRecords={sortedStudents.length}
              onPageChange={onPageChange}
              template="PrevPageLink CurrentPageReport NextPageLink"
              prevPageLinkIcon={<i className="nc_icon-nc_left icon-sm mr-2 text-primary cursor-pointer"></i>}
              nextPageLinkIcon={<i className="nc_icon-nc_right icon-sm ml-2 text-primary cursor-pointer"></i>}
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              className="p-0"
            />
          </div>
        </>
      ) : (
        <div className="flex h-full justify-content-center align-items-center px-4">
          <p className="md:text-lg text-sm text-center">
            There are no students with skill challenge attempts without passing this week.
          </p>
        </div>
      )}
    </div>
  );
};

export default SkillChallengeAttemptsWithoutPassing;
